import { createSlice, nanoid } from '@reduxjs/toolkit';

// Payment method types
export const PAYMENT_METHOD_TYPE = {
  CREDIT_CARD: 'credit_card',
  DEBIT_CARD: 'debit_card',
  DIGITAL_WALLET: 'digital_wallet',
  BANK_TRANSFER: 'bank_transfer',
  CASH: 'cash',
  CRYPTOCURRENCY: 'cryptocurrency'
};

// Payment status constants
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  CANCELLED: 'cancelled'
};

// Transaction types
export const TRANSACTION_TYPE = {
  RIDE_PAYMENT: 'ride_payment',
  REFUND: 'refund',
  COMMISSION: 'commission',
  BONUS: 'bonus',
  PENALTY: 'penalty',
  WITHDRAWAL: 'withdrawal',
  DEPOSIT: 'deposit'
};

// Generate realistic payment methods data
const generatePaymentMethods = () => {
  return [
    {
      id: nanoid(),
      name: 'Credit/Debit Cards',
      type: PAYMENT_METHOD_TYPE.CREDIT_CARD,
      provider: 'Stripe',
      isActive: true,
      processingFee: 2.9,
      processingFeeType: 'percentage',
      minimumAmount: 1.00,
      maximumAmount: 10000.00,
      supportedCurrencies: ['USD', 'EUR', 'GBP'],
      averageProcessingTime: '2-3 seconds',
      configuration: {
        publicKey: 'pk_test_***',
        webhookUrl: 'https://api.cabtrack.com/webhooks/stripe',
        supportedCards: ['visa', 'mastercard', 'amex', 'discover']
      },
      statistics: {
        totalTransactions: 15420,
        totalVolume: 2847392.50,
        successRate: 98.7,
        averageTransactionAmount: 24.85
      },
      createdAt: '2024-01-15T10:30:00.000Z',
      lastModified: '2024-12-01T14:20:00.000Z'
    },
    {
      id: nanoid(),
      name: 'PayPal',
      type: PAYMENT_METHOD_TYPE.DIGITAL_WALLET,
      provider: 'PayPal',
      isActive: true,
      processingFee: 3.49,
      processingFeeType: 'percentage',
      minimumAmount: 0.50,
      maximumAmount: 5000.00,
      supportedCurrencies: ['USD', 'EUR', 'GBP', 'CAD'],
      averageProcessingTime: '5-10 seconds',
      configuration: {
        clientId: 'AYz***',
        webhookUrl: 'https://api.cabtrack.com/webhooks/paypal',
        environment: 'sandbox'
      },
      statistics: {
        totalTransactions: 8934,
        totalVolume: 1245678.90,
        successRate: 97.2,
        averageTransactionAmount: 18.95
      },
      createdAt: '2024-02-01T09:15:00.000Z',
      lastModified: '2024-11-15T16:45:00.000Z'
    },
    {
      id: nanoid(),
      name: 'Apple Pay',
      type: PAYMENT_METHOD_TYPE.DIGITAL_WALLET,
      provider: 'Apple',
      isActive: true,
      processingFee: 2.9,
      processingFeeType: 'percentage',
      minimumAmount: 1.00,
      maximumAmount: 3000.00,
      supportedCurrencies: ['USD', 'EUR', 'GBP'],
      averageProcessingTime: '1-2 seconds',
      configuration: {
        merchantId: 'merchant.com.cabtrack',
        certificatePath: '/certs/apple-pay.pem'
      },
      statistics: {
        totalTransactions: 12567,
        totalVolume: 1876543.20,
        successRate: 99.1,
        averageTransactionAmount: 22.40
      },
      createdAt: '2024-03-10T11:00:00.000Z',
      lastModified: '2024-12-05T08:30:00.000Z'
    },
    {
      id: nanoid(),
      name: 'Google Pay',
      type: PAYMENT_METHOD_TYPE.DIGITAL_WALLET,
      provider: 'Google',
      isActive: true,
      processingFee: 2.9,
      processingFeeType: 'percentage',
      minimumAmount: 1.00,
      maximumAmount: 3000.00,
      supportedCurrencies: ['USD', 'EUR', 'GBP'],
      averageProcessingTime: '1-2 seconds',
      configuration: {
        merchantId: 'BCR2DN***',
        environment: 'TEST'
      },
      statistics: {
        totalTransactions: 9876,
        totalVolume: 1432109.80,
        successRate: 98.9,
        averageTransactionAmount: 21.75
      },
      createdAt: '2024-03-15T13:45:00.000Z',
      lastModified: '2024-11-28T10:15:00.000Z'
    },
    {
      id: nanoid(),
      name: 'Cash Payments',
      type: PAYMENT_METHOD_TYPE.CASH,
      provider: 'Manual',
      isActive: true,
      processingFee: 0,
      processingFeeType: 'fixed',
      minimumAmount: 0.50,
      maximumAmount: 500.00,
      supportedCurrencies: ['USD'],
      averageProcessingTime: 'Instant',
      configuration: {
        requiresDriverConfirmation: true,
        allowsChange: true
      },
      statistics: {
        totalTransactions: 5432,
        totalVolume: 89765.40,
        successRate: 100.0,
        averageTransactionAmount: 16.52
      },
      createdAt: '2024-01-01T00:00:00.000Z',
      lastModified: '2024-12-01T12:00:00.000Z'
    }
  ];
};

// Generate realistic transaction history
const generateTransactionHistory = () => {
  const transactions = [];
  const paymentMethods = ['Credit Card', 'PayPal', 'Apple Pay', 'Google Pay', 'Cash'];
  const statuses = Object.values(PAYMENT_STATUS);
  const types = Object.values(TRANSACTION_TYPE);

  for (let i = 0; i < 200; i++) {
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 90));
    
    const amount = parseFloat((Math.random() * 100 + 5).toFixed(2));
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const type = types[Math.floor(Math.random() * types.length)];
    
    transactions.push({
      id: nanoid(),
      transactionId: `TXN_${Date.now()}_${i}`,
      tripId: type === TRANSACTION_TYPE.RIDE_PAYMENT ? nanoid() : null,
      userId: nanoid(),
      driverId: type === TRANSACTION_TYPE.RIDE_PAYMENT ? nanoid() : null,
      type,
      amount,
      currency: 'USD',
      status,
      paymentMethod: paymentMethods[Math.floor(Math.random() * paymentMethods.length)],
      processingFee: parseFloat((amount * 0.029).toFixed(2)),
      netAmount: parseFloat((amount - (amount * 0.029)).toFixed(2)),
      description: type === TRANSACTION_TYPE.RIDE_PAYMENT ? 
        `Payment for ride from ${['Downtown', 'Airport', 'Mall', 'Station'][Math.floor(Math.random() * 4)]}` :
        `${type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} transaction`,
      metadata: {
        gateway: ['stripe', 'paypal', 'apple', 'google'][Math.floor(Math.random() * 4)],
        reference: `REF_${Math.random().toString(36).substr(2, 9).toUpperCase()}`
      },
      createdAt: date.toISOString(),
      processedAt: status === PAYMENT_STATUS.COMPLETED ? 
        new Date(date.getTime() + Math.random() * 300000).toISOString() : null,
      failureReason: status === PAYMENT_STATUS.FAILED ? 
        ['Insufficient funds', 'Card declined', 'Network error', 'Invalid card'][Math.floor(Math.random() * 4)] : null
    });
  }

  return transactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

const initialState = {
  paymentMethods: generatePaymentMethods(),
  transactions: generateTransactionHistory(),
  statistics: {
    totalRevenue: 6491484.80,
    totalTransactions: 52229,
    averageTransactionValue: 24.32,
    successRate: 98.4,
    totalProcessingFees: 188547.66,
    netRevenue: 6302937.14,
    lastUpdated: new Date().toISOString()
  }
};

const paymentSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    addPaymentMethod(state, action) {
      state.paymentMethods.unshift({
        id: nanoid(),
        ...action.payload,
        isActive: true,
        statistics: {
          totalTransactions: 0,
          totalVolume: 0,
          successRate: 0,
          averageTransactionAmount: 0
        },
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString()
      });
    },

    updatePaymentMethod(state, action) {
      const { id, ...updates } = action.payload;
      const methodIndex = state.paymentMethods.findIndex(m => m.id === id);
      if (methodIndex !== -1) {
        state.paymentMethods[methodIndex] = {
          ...state.paymentMethods[methodIndex],
          ...updates,
          lastModified: new Date().toISOString()
        };
      }
    },

    togglePaymentMethod(state, action) {
      const { id } = action.payload;
      const method = state.paymentMethods.find(m => m.id === id);
      if (method) {
        method.isActive = !method.isActive;
        method.lastModified = new Date().toISOString();
      }
    },

    addTransaction(state, action) {
      state.transactions.unshift({
        id: nanoid(),
        transactionId: `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...action.payload,
        createdAt: new Date().toISOString()
      });

      // Keep only last 1000 transactions
      if (state.transactions.length > 1000) {
        state.transactions = state.transactions.slice(0, 1000);
      }
    },

    updateTransactionStatus(state, action) {
      const { transactionId, status, failureReason } = action.payload;
      const transaction = state.transactions.find(t => t.transactionId === transactionId);
      if (transaction) {
        transaction.status = status;
        transaction.processedAt = new Date().toISOString();
        if (failureReason) {
          transaction.failureReason = failureReason;
        }
      }
    },

    updateStatistics(state, action) {
      state.statistics = {
        ...state.statistics,
        ...action.payload,
        lastUpdated: new Date().toISOString()
      };
    }
  }
});

export const {
  addPaymentMethod,
  updatePaymentMethod,
  togglePaymentMethod,
  addTransaction,
  updateTransactionStatus,
  updateStatistics
} = paymentSlice.actions;

export default paymentSlice.reducer;
